import { useEffect, useState } from "react";
import { captureException } from "@sentry/nextjs";
import NextImage from "next/image";
import { Box, Stack, AspectRatio, Text } from "@chakra-ui/react";

import Container from "components/Chakra/Container";
import useApiEndpoint from "hooks/useApiEndpoint";
import useRouter from "hooks/useRouter";
import GetTopBoardApiEndpoint from "utils/offer/api/GetTopBoard";
import { ITopBoardOffer, IOfferPreview } from "utils/offer/types";
import { DISPOSITIONS, IDispositionItemConfig } from "config/dispositions";
import IconPin from "components/Chakra/Icons/Pin";
import CustomSpinner from "components/CustomSpinner";
import ResponsiveGrid from "components/Chakra/ResponsiveGrid";
import CustomLink from "components/Chakra/CustomLink";

interface IProps {
  city: string;
}

const List: React.FC<IProps> = ({ city }) => {
  const [data, setData] = useState<Array<ITopBoardOffer>>([]);
  const [isLoading, setLoading] = useState<boolean>(true);
  const { callEndpoint: getTopBoardApi } = useApiEndpoint(
    GetTopBoardApiEndpoint,
  );
  const { navigateTo } = useRouter();

  useEffect(() => {
    if (window.location.pathname.includes("/platba/")) {
      return;
    }
    setLoading(true);
    const callTopBoardApi = async () => {
      try {
        const { data: topBoardData } = await getTopBoardApi({ city });
        if (topBoardData) {
          setData(topBoardData);
          setLoading(false);
        } else {
          navigateTo("/premium-vyhody/[hook]", { hook: "top" });
        }
      } catch (err) {
        captureException(err);
      }
    };

    callTopBoardApi();
  }, [city, getTopBoardApi, navigateTo]);

  return (
    <Container mb="96px" mt="48px">
      <ResponsiveGrid
        templateColumns={isLoading ? "1fr" : "repeat(3, 1fr)"}
        gap="30px"
        mobile={{ templateColumns: "1fr" }}
      >
        {isLoading ? (
          <CustomSpinner w="100%" />
        ) : (
          data.map((el) => {
            const offer: IOfferPreview = {
              ...el,
              description: "",
              geocoords: { lat: 0, lng: 0 },
              monthlyFeesPrice: 0,
              depositPrice: 0,
              commissionPrice: 0,
              priceNote: "",
              floorLevel: null,
              conveniences: [],
              availableFrom: null,
              adminUrl: "",
              showScamWarn: false,
              score: "",
              specialFlag: null,
              isContacted: false,
            };
            const dispId = el.dispositionId;
            const dispObj = DISPOSITIONS.find((dis) =>
              dis.apiIds.includes(dispId),
            ) as IDispositionItemConfig;

            return (
              <CustomLink
                key={el.id}
                trackEventPayload={{
                  "dl.eCat": "TOP_nastenka",
                  "dl.eAct": "inzerat_click",
                  "dl.eLab": city,
                }}
                to="/inzerat/[seo]/[id]"
                routeProps={{
                  seo: offer.seo,
                  id: offer.id.toString(),
                }}
              >
                <Box
                  boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
                  __css={{
                    "&:hover > .title": {
                      bg: "rgba(255, 255, 255, 0.9)",

                      p: { color: "black" },
                    },
                  }}
                >
                  <Stack
                    className="title"
                    w="100%"
                    h="40px"
                    mb="-40px"
                    transition="background 333ms"
                    zIndex="999"
                    direction="row"
                    align="center"
                    position="relative"
                    px="20px"
                    justify="space-between"
                  >
                    <Text
                      color="ud-white"
                      transition="color 0.4s"
                      _hover={{ color: "ud-black" }}
                    >
                      {`${dispObj?.translationId || "Spolubydlení"}, ${
                        el.area
                      } m`}
                      &sup2;
                    </Text>
                    <Text
                      color="ud-white"
                      transition="color 0.4s"
                      _hover={{ color: "ud-black" }}
                    >{`${el.rentalPrice} Kč`}</Text>
                  </Stack>

                  <AspectRatio w="100%" ratio={4 / 3}>
                    <NextImage
                      src={el.photos[0].path}
                      alt="Ilustrační foto bytu"
                      fill
                      style={{ objectFit: "cover" }}
                    />
                  </AspectRatio>
                  <Stack direction="row" align="center" p="10px">
                    <IconPin fillWith="ud-grey-200" boxSize="10px" />
                    <Stack spacing="0">
                      <Text textAlign="left" variant="small_2" pl="10px">
                        {el.street?.label}
                      </Text>
                      <Text textAlign="left" variant="small_2" pl="10px">
                        {el.village.label} - {el.villagePart?.label}
                      </Text>
                    </Stack>
                  </Stack>
                </Box>
              </CustomLink>
            );
          })
        )}
      </ResponsiveGrid>
    </Container>
  );
};

export default List;
